import { FormEvent, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Copy, FileText, Loader2, Megaphone, PlayCircle, Power, Send, Users } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';

type PatientOption = { id: string; firstName?: string; lastName?: string; name?: string; email?: string | null; phone?: string | null };
type Campaign = {
  id: string;
  name: string;
  channel: 'EMAIL' | 'SMS' | 'WHATSAPP';
  status: string;
  audienceFilters: Record<string, unknown>;
  messageTemplate: string;
  redemptionTrackingCode?: string | null;
  recipientCount: number;
  suppressedCount: number;
  redeemedCount: number;
};
type AdvancedMarketing = {
  reviewRequests: Array<{ id: string; patientId: string; channel: string; status: string }>;
  referrals: Array<{ id: string; code: string; referrerPatientId: string; status: string }>;
  journeys: Array<{ id: string; name: string; status: string }>;
};

const channels = ['EMAIL', 'SMS', 'WHATSAPP'] as const;

function patientName(patient: PatientOption) {
  return patient.name || `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || patient.id;
}

export default function MarketingCampaignsPage() {
  const { fetchJson } = useApiClient();
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [advanced, setAdvanced] = useState<AdvancedMarketing | null>(null);
  const [consentPatientId, setConsentPatientId] = useState('');
  const [consentChannel, setConsentChannel] = useState<(typeof channels)[number]>('EMAIL');
  const [consented, setConsented] = useState(true);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    channel: 'EMAIL' as (typeof channels)[number],
    audienceFilters: '{}',
    messageTemplate: '',
    suppressionRules: '{"requireConsent":true}',
    redemptionTrackingCode: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [automationTemplates, setAutomationTemplates] = useState([
    { name: 'Recall reminder', trigger: 'Last visit over 6 months', enabled: true },
    { name: 'Birthday SMS', trigger: 'Client birthday', enabled: true },
    { name: 'Debt collection', trigger: 'Invoice overdue 14 days', enabled: false },
    { name: 'Aftercare', trigger: 'Appointment completed', enabled: true },
    { name: 'Lead follow-up', trigger: 'Lead created', enabled: true },
  ]);
  const [newsletterHtml, setNewsletterHtml] = useState('<h2>Monthly clinic news</h2><p>Add offers, aftercare, and updates here.</p><p>{{unsubscribe_link}}</p>');
  const [targetingLabels, setTargetingLabels] = useState(['VIP', 'High risk', 'Insurance pending']);
  const [reviewText, setReviewText] = useState('');
  const [reviewResponse, setReviewResponse] = useState('');
  const [reviewAiBusy, setReviewAiBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [patientPayload, campaignPayload, advancedPayload] = await Promise.all([
        fetchJson<{ data: PatientOption[] }>('/patients?page=1&pageSize=100'),
        fetchJson<{ data: Campaign[] }>('/marketing/campaigns'),
        fetchJson<AdvancedMarketing>('/marketing/advanced'),
      ]);
      setPatients(patientPayload.data || []);
      setCampaigns(campaignPayload.data || []);
      setAdvanced(advancedPayload);
      setConsentPatientId(patientPayload.data?.[0]?.id || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load marketing data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function saveConsent(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await fetchJson('/marketing/consents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientId: consentPatientId, channel: consentChannel, consented, source: 'staff' }),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save consent.');
    } finally {
      setSaving(false);
    }
  }

  async function createCampaign(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await fetchJson('/marketing/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...campaignForm,
          audienceFilters: JSON.parse(campaignForm.audienceFilters || '{}'),
          suppressionRules: JSON.parse(campaignForm.suppressionRules || '{}'),
          redemptionTrackingCode: campaignForm.redemptionTrackingCode || undefined,
        }),
      });
      setCampaignForm({ name: '', channel: 'EMAIL', audienceFilters: '{}', messageTemplate: '', suppressionRules: '{"requireConsent":true}', redemptionTrackingCode: '' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create campaign.');
    } finally {
      setSaving(false);
    }
  }

  async function sendCampaign(id: string) {
    setError('');
    try {
      await fetchJson(`/marketing/campaigns/${id}/send`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send campaign.');
    }
  }

  async function generateReviewResponse() {
    if (!reviewText.trim()) {
      setError('Add review text before generating a response.');
      return;
    }
    setReviewAiBusy(true);
    setError('');
    try {
      const result = await fetchJson<{ contentText: string }>('/agent/echo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task: 'review-response', reviewText, tone: 'warm, concise, privacy-safe' }),
      });
      setReviewResponse(result.contentText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Review response assistant failed.');
    } finally {
      setReviewAiBusy(false);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Marketing</p>
        <h1 className="text-2xl font-semibold text-slate-900">Consent and campaigns</h1>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <form onSubmit={saveConsent} className="grid gap-3 rounded-lg border border-slate-200 bg-white p-5 md:grid-cols-4">
        <Field label="Patient">
          <select required value={consentPatientId} onChange={(event) => setConsentPatientId(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2">
            {patients.map((patient) => <option key={patient.id} value={patient.id}>{patientName(patient)}</option>)}
          </select>
        </Field>
        <Field label="Channel">
          <select value={consentChannel} onChange={(event) => setConsentChannel(event.target.value as typeof consentChannel)} className="w-full rounded-md border border-slate-300 px-3 py-2">
            {channels.map((channel) => <option key={channel}>{channel}</option>)}
          </select>
        </Field>
        <Field label="Consent">
          <select value={String(consented)} onChange={(event) => setConsented(event.target.value === 'true')} className="w-full rounded-md border border-slate-300 px-3 py-2">
            <option value="true">Consented</option>
            <option value="false">Unsubscribed</option>
          </select>
        </Field>
        <div className="flex items-end">
          <button disabled={saving || !consentPatientId} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            <Megaphone size={16} />
            Save consent
          </button>
        </div>
      </form>

      <form onSubmit={createCampaign} className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
        <div className="text-sm font-semibold text-slate-900">Create campaign</div>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Name"><input required value={campaignForm.name} onChange={(event) => setCampaignForm((prev) => ({ ...prev, name: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
          <Field label="Channel"><select value={campaignForm.channel} onChange={(event) => setCampaignForm((prev) => ({ ...prev, channel: event.target.value as typeof campaignForm.channel }))} className="w-full rounded-md border border-slate-300 px-3 py-2">{channels.map((channel) => <option key={channel}>{channel}</option>)}</select></Field>
          <Field label="Redemption code"><input value={campaignForm.redemptionTrackingCode} onChange={(event) => setCampaignForm((prev) => ({ ...prev, redemptionTrackingCode: event.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
        </div>
        <Field label="Audience filters JSON"><textarea value={campaignForm.audienceFilters} onChange={(event) => setCampaignForm((prev) => ({ ...prev, audienceFilters: event.target.value }))} className="min-h-20 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm" /></Field>
        <Field label="Message template"><textarea required value={campaignForm.messageTemplate} onChange={(event) => setCampaignForm((prev) => ({ ...prev, messageTemplate: event.target.value }))} className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" /></Field>
        <div className="flex flex-wrap gap-2">
          {['Recall reminder', 'Birthday SMS', 'Debt collection', 'Aftercare', 'Lead follow-up'].map((template) => (
            <button
              key={template}
              type="button"
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setCampaignForm((prev) => ({ ...prev, name: prev.name || template, messageTemplate: `${template}: Hi {{client.firstName}}, this is a quick message from the clinic.\n{{unsubscribe_link}}` }))}
            >
              {template}
            </button>
          ))}
          <button
            type="button"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => setCampaignForm((prev) => ({ ...prev, messageTemplate: `${prev.messageTemplate}\n{{unsubscribe_link}}` }))}
          >
            Insert unsubscribe
          </button>
        </div>
        <Field label="Suppression rules JSON"><textarea value={campaignForm.suppressionRules} onChange={(event) => setCampaignForm((prev) => ({ ...prev, suppressionRules: event.target.value }))} className="min-h-20 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm" /></Field>
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><Users size={16} /> Client groups and labels</div>
          <div className="flex flex-wrap gap-2">
            {targetingLabels.map((label) => (
              <button
                key={label}
                type="button"
                className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700"
                onClick={() => setCampaignForm((prev) => ({ ...prev, audienceFilters: JSON.stringify({ labels: [label] }, null, 2) }))}
              >
                {label}
              </button>
            ))}
            <button type="button" className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700" onClick={() => setTargetingLabels((current) => [...current, `Group ${current.length + 1}`])}>Add group</button>
          </div>
        </div>
        <button disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          <Megaphone size={16} />
          Save campaign
        </button>
      </form>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Campaign</th><th className="px-4 py-3">Audience</th><th className="px-4 py-3">Redemptions</th><th className="px-4 py-3">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td className="px-4 py-5 text-slate-500" colSpan={4}>Loading campaigns...</td></tr> : campaigns.length ? campaigns.map((campaign) => (
              <tr key={campaign.id}>
                <td className="px-4 py-3"><div className="font-semibold text-slate-900">{campaign.name}</div><div className="text-slate-500">{campaign.channel} · {campaign.status}</div></td>
                <td className="px-4 py-3 text-slate-600">{campaign.recipientCount} recipients · {campaign.suppressedCount} suppressed</td>
                <td className="px-4 py-3 text-slate-600">{campaign.redeemedCount}{campaign.redemptionTrackingCode ? ` · ${campaign.redemptionTrackingCode}` : ''}</td>
                <td className="px-4 py-3"><button type="button" onClick={() => void sendCampaign(campaign.id)} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium"><Send size={16} /> Send</button></td>
              </tr>
            )) : <tr><td className="px-4 py-5 text-slate-500" colSpan={4}>No campaigns yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          title="Automation builder"
          rows={automationTemplates.map((row) => [row.name, `${row.trigger} · ${row.enabled ? 'Enabled' : 'Disabled'}`])}
        />
        <div className="rounded-lg border border-slate-200 bg-white p-5 xl:col-span-2">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Automation templates and logs</h2>
              <p className="text-xs text-slate-500">Clone, enable, disable, and review recent automation activity.</p>
            </div>
            <button type="button" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm"><PlayCircle size={16} /> New automation</button>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {automationTemplates.map((automation, index) => (
              <div key={automation.name} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900">{automation.name}</span>
                  <span className={automation.enabled ? 'text-emerald-700' : 'text-slate-500'}>{automation.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{automation.trigger} · Last run 2h ago · 18 sent</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs" onClick={() => setAutomationTemplates((current) => [...current, { ...automation, name: `${automation.name} copy` }])}><Copy size={13} /> Clone</button>
                  <button type="button" className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs" onClick={() => setAutomationTemplates((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: !item.enabled } : item))}><Power size={13} /> Toggle</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center gap-2">
          <FileText size={18} />
          <h2 className="text-sm font-semibold text-slate-900">HTML newsletter editor</h2>
        </div>
        <textarea className="min-h-40 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm" value={newsletterHtml} onChange={(event) => setNewsletterHtml(event.target.value)} />
        <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600">
          Broadcast report preview: 2,340 delivered · 48% open rate · 12% click rate · unsubscribe link present.
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Review Response Assistant</h2>
          <div className="mt-3 space-y-3">
            <textarea
              className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={reviewText}
              onChange={(event) => setReviewText(event.target.value)}
              placeholder="Paste a client review"
            />
            <button type="button" onClick={() => void generateReviewResponse()} disabled={reviewAiBusy} className="inline-flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800 disabled:opacity-50">
              {reviewAiBusy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} Draft reply
            </button>
            {reviewResponse ? <textarea className="min-h-28 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={reviewResponse} onChange={(event) => setReviewResponse(event.target.value)} /> : null}
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Review requests</p>
            <div className="mt-2 divide-y divide-slate-100">
              {(advanced?.reviewRequests || []).length ? (advanced?.reviewRequests || []).map((row) => (
                <div key={row.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-slate-700">{row.patientId}</span>
                  <span className="text-slate-500">{row.channel} · {row.status}</span>
                </div>
              )) : <div className="py-3 text-sm text-slate-500">No rows yet.</div>}
            </div>
          </div>
        </div>
        <Panel title="Referrals" rows={(advanced?.referrals || []).map((row) => [row.code, row.status])} />
        <Panel title="Automated journeys" rows={(advanced?.journeys || []).map((row) => [row.name, row.status])} />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block text-sm"><span className="mb-1 block font-medium text-slate-700">{label}</span>{children}</label>;
}

function Panel({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <div className="mt-3 divide-y divide-slate-100">
        {rows.length ? rows.map(([label, value], index) => (
          <div key={`${label}-${index}`} className="flex items-center justify-between py-2 text-sm">
            <span className="text-slate-700">{label}</span>
            <span className="text-slate-500">{value}</span>
          </div>
        )) : <div className="py-3 text-sm text-slate-500">No rows yet.</div>}
      </div>
    </div>
  );
}
